import type { DeliverableDto } from "../../shared/dto";
import { WorkingAvatar } from "../WorkingAvatar";
import { GateDocCard } from "./DocumentCards";
import {
  ActionError,
  FocusedSalesPage,
  MaterialFeedbackSummary,
  focusedState,
  staffReturnedReason,
  type DeliverySectionContentProps
} from "./shared";

export function StrategyPage(props: DeliverySectionContentProps) {
  const state = focusedState(props, "strategy");
  const strategy = mergeStrategyDeliverables(props.deliverables, props.t("sales.strategy.title"));
  const approved = strategy?.status === "approved";
  const backfillReview = state === "next"
    && props.sections.find((section) => section.key === "q2")?.state === "locked";
  if (state === "working" && !strategy) {
    return (
      <FocusedSalesPage page="strategy" state={state} t={props.t}>
        <StrategyWaitingState
          material="strategy"
          profilePhotoUrl={props.profilePhotoUrl}
          titleKey="sales.preparingWait.strategy.title"
          copyKey="sales.preparingWait.strategy.copy"
          etaKey="sales.preparingWait.strategy.eta"
          t={props.t}
        />
      </FocusedSalesPage>
    );
  }
  return (
    <FocusedSalesPage
      page="strategy"
      state={state}
      copyKey={approved ? "sales.pages.strategy.doneCopy" : undefined}
      t={props.t}
    >
      {!approved && (
        <>
          {backfillReview && (
            <article className="focused-sales-intro">
              <p className="focused-sales-note">{props.t("sales.pages.strategy.backfillReview")}</p>
            </article>
          )}
        </>
      )}
      <div className="focused-sales-content"><StrategyCard {...props} strategy={strategy} /></div>
      {!approved && strategy && props.membershipRole === "owner" && (
        <>
          <StrategyReviewGuide t={props.t} />
          <MaterialFeedbackSummary
            deliverableId={strategy.id}
            version={strategy.version}
            refreshVersion={props.feedbackRevision}
            onVersionAdvanced={props.onReloadData}
            t={props.t}
          />
        </>
      )}
      <ActionError t={props.t} issue={props.actionError} />
    </FocusedSalesPage>
  );
}

export function StrategyReviewGuide(props: { t: (key: string) => string }) {
  return (
    <section className="strategy-review-guide" data-strategy-review-guide="true" aria-labelledby="strategy-review-guide-title">
      <p className="card-kicker">{props.t("sales.strategy.reviewGuide.eyebrow")}</p>
      <h2 id="strategy-review-guide-title">{props.t("sales.strategy.reviewGuide.title")}</h2>
      <div className="strategy-review-guide-list">
        <div>
          <span className="strategy-review-guide-mark approve" aria-hidden="true">✓</span>
          <div>
            <h3>{props.t("sales.strategy.reviewGuide.approveTitle")}</h3>
            <p>{props.t("sales.strategy.reviewGuide.approveCopy")}</p>
          </div>
        </div>
        <div>
          <span className="strategy-review-guide-mark feedback" aria-hidden="true">●</span>
          <div>
            <h3>{props.t("sales.strategy.reviewGuide.feedbackTitle")}</h3>
            <p>{props.t("sales.strategy.reviewGuide.feedbackCopy")}</p>
          </div>
        </div>
      </div>
    </section>
  );
}

export function StrategyWaitingState(props: {
  kind?: "preparing" | "approved";
  material?: "strategy" | "salespage" | "emails" | "webinar";
  profilePhotoUrl: string | null;
  titleKey: string;
  copyKey: string;
  etaKey: string;
  t: (key: string) => string;
}) {
  const kind = props.kind ?? "preparing";
  return (
    <article
      className="strategy-approved-wait is-active"
      data-strategy-waiting={kind}
      data-preparing-material={kind === "preparing" ? props.material : undefined}
      role="status"
      aria-live="polite"
    >
      <WorkingAvatar size="stage" profilePhotoUrl={props.profilePhotoUrl} />
      <div>
        <p className="card-kicker">{props.t(props.titleKey)}</p>
        <p className="strategy-approved-calm">{props.t(props.copyKey)}</p>
        {/* F92: the production timeline (steps, "Krok X z Y", minutes) belongs
            to the PARTNER, who still has to review and can send the material
            back. The client always reads the calm promise: 2-3 working days
            and a notification when their feedback is needed. */}
        <p className="strategy-approved-eta">{props.t(props.etaKey)}</p>
      </div>
    </article>
  );
}

export function mergeStrategyDeliverables(
  deliverables: DeliverableDto[],
  title: string
): DeliverableDto | null {
  const documents = deliverables
    .filter((item) => item.kind === "audit" || item.kind === "plan")
    .sort((left, right) => (left.kind === right.kind ? left.version - right.version : left.kind === "audit" ? -1 : 1));
  if (documents.length === 0) return null;
  const representative = documents.find((item) => item.kind === "plan") ?? documents[0];
  const approved = documents.every((item) => item.status === "approved");
  const contentMd = documents.length === 1
    ? documents[0].contentMd
    : documents
      .filter((item) => item.contentMd)
      .map((item) => `*** ${item.title}\n\n${item.contentMd}`)
      .join("\n\n");
  return {
    ...representative,
    id: representative.id,
    kind: "plan",
    version: Math.max(...documents.map((item) => item.version)),
    title,
    status: approved ? "approved" : "review",
    approvedBy: approved ? representative.approvedBy : null,
    approvedAt: approved ? representative.approvedAt : null,
    contentMd
  };
}

export function StrategyCard(props: DeliverySectionContentProps & { strategy: DeliverableDto | null }) {
  const strategy = props.strategy;
  if (!strategy) {
    return (
      <article className="deliverable-card doc-tile" data-deliverable-kind="strategy">
        <div className="deliverable-card-heading">
          <div><p className="card-kicker">{props.t("sales.kind.plan")}</p><h2>{props.t("sales.preparingTitle")}</h2></div>
        </div>
        <div className="deliverable-empty"><span className="loading-pulse" /><p>{props.t("sales.preparing.plan")}</p></div>
      </article>
    );
  }
  return (
    <GateDocCard
      kind="plan"
      dataKind="strategy"
      deliverable={strategy}
      displayKicker={props.t("sales.pages.strategy.cardEyebrow")}
      displayTitle={props.t("sales.pages.strategy.cardTitle")}
      canAct={props.membershipRole === "owner"}
      canApprove
      ctaKey={strategy.status === "approved" ? "sales.strategy.reviewDownload" : undefined}
      approvalHintKey="sales.strategy.approvalHint"
      showActionHint={false}
      busyAction={props.busyAction}
      csrfToken={props.csrfToken}
      staffReturnedReason={staffReturnedReason(props.resources, "strategy")}
      t={props.t}
      onApprove={() => props.onGateApprove?.(strategy.id)}
      onFeedback={(payload) => Promise.resolve(props.onGateFeedback?.(strategy.id, payload) ?? false)}
    />
  );
}
